import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, KeyRound, CheckCircle2, AlertCircle, Mail, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { saveOnboardingState, getOnboardingState } from '@/lib/onboarding-store';
import { sendResetLink } from '@/lib/api';
import { isAdminSession } from '@/lib/user-role';

type PageState = 'loading' | 'form' | 'success' | 'error';

export function ResetPassword() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [linkType, setLinkType] = useState<'recovery' | 'invite' | null>(null);
  const [isTeacherAccount, setIsTeacherAccount] = useState(false);
  const [isSchoolContact, setIsSchoolContact] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Expired-link resend state
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    // Supabase appends auth params to the URL hash after redirect
    // e.g. #access_token=xxx&refresh_token=yyy&type=recovery
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type') as 'recovery' | 'invite' | null;

    if (!accessToken || !refreshToken) {
      setPageState('error');
      return;
    }

    setLinkType(type);

    // Exchange the tokens to establish a session
    supabase.auth
      .setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(({ error }) => {
        if (error) {
          setPageState('error');
        } else {
          setPageState('form');
        }
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message);
      setIsLoading(false);
      return;
    }

    // Get the current user + session to read metadata and determine where to redirect
    const { data: { user } } = await supabase.auth.getUser();
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    // Differentiate teachers, secondary contacts, and primary school admins by role/metadata.
    const isTeacher = user?.user_metadata?.role === 'teacher';
    const metaSchoolId = user?.user_metadata?.schoolId as string | undefined;
    // Secondary contacts are ADMINS whose schoolId points at the primary owner's
    // id. Gating on isAdminSession keeps a non-admin (e.g. a role-less teacher)
    // out of the admin portal here — mirrors the SchoolLogin isolation guard.
    const isSecondaryContact = !isTeacher && isAdminSession(currentSession) && !!metaSchoolId && metaSchoolId !== user?.id;
    setIsTeacherAccount(isTeacher);
    setIsSchoolContact(isSecondaryContact);

    if (isTeacher) {
      // Teacher invite/reset — set up their session and go straight to app
      const firstName  = (user.user_metadata.firstName  as string | undefined) ?? '';
      const lastName   = (user.user_metadata.lastName   as string | undefined) ?? '';
      const department = (user.user_metadata.department as string | undefined) ?? '';
      if (currentSession?.access_token) {
        sessionStorage.setItem('cmc_teacher_token', currentSession.access_token);
      }
      if (currentSession?.user?.id) {
        sessionStorage.setItem('cmc_teacher_id', currentSession.user.id);
      }
      saveOnboardingState({
        isComplete: true,
        userType: 'teacher',
        teacherName: `${firstName} ${lastName}`.trim() || user.email || 'Teacher',
        teacherEmail: user.email ?? '',
        teacherDepartment: department,
      });
      sessionStorage.setItem('cmc_first_login', '1');
    } else if (isSecondaryContact) {
      // Secondary contact invite — keep session, set up as school admin, go straight to app.
      // effectiveSchoolId is the primary admin's ID (from metadata.schoolId).
      const effectiveSchoolId = (metaSchoolId !== user.id) ? metaSchoolId : user.id;
      if (currentSession?.access_token) {
        sessionStorage.setItem('cmc_school_token', currentSession.access_token);
      }
      saveOnboardingState({
        ...getOnboardingState(),
        isComplete: true,
        userType: 'school',
        supabaseUserId: effectiveSchoolId,
      });
    } else {
      // Primary school admin password reset — sign out so they log in with new credentials
      await supabase.auth.signOut();
    }

    setPageState('success');
  };

  // ── Loading ───────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <p className="text-white/70">Verifying your link…</p>
        </div>
      </div>
    );
  }

  // ── Invalid / expired link ────────────────────────────────────────
  if (pageState === 'error') {
    const handleResend = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!resendEmail.trim()) return;
      setResendError('');
      setResendLoading(true);
      try {
        await sendResetLink(resendEmail.trim());
        setResendSent(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('400')) {
          setResendError('Please enter a valid email address.');
        } else {
          setResendError('Could not send the link. Please try again or contact support@covermyclass.com.');
        }
      } finally {
        setResendLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {linkType === 'recovery' ? 'Reset Link Expired' : 'Invitation Link Expired'}
            </h2>
            <p className="text-muted-foreground text-sm">
              This link is no longer valid, {linkType === 'invite' ? 'invite' : 'password reset'} links expire after 24 hours for security.
            </p>
          </div>

          {resendSent ? (
            <div className="bg-success/10 border border-success/20 rounded-xl p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-success mx-auto mb-2" />
              <p className="font-semibold text-foreground mb-1">Check your inbox</p>
              <p className="text-sm text-muted-foreground">
                A new link has been sent to <strong>{resendEmail}</strong>. Check your spam folder if it doesn't arrive within a minute.
              </p>
            </div>
          ) : (
            <form onSubmit={handleResend} className="space-y-3">
              <p className="text-sm font-semibold text-foreground">Get a new link</p>
              <Input
                type="email"
                placeholder="Enter your email address"
                value={resendEmail}
                onChange={e => setResendEmail(e.target.value)}
                required
                autoFocus
              />
              {resendError && (
                <p className="text-sm text-destructive">{resendError}</p>
              )}
              <Button type="submit" className="w-full" disabled={resendLoading}>
                {resendLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : 'Send new link'}
              </Button>
            </form>
          )}

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-4">
            <Mail className="w-4 h-4" />
            <span>Need help?{' '}
              <a href="mailto:support@covermyclass.com" className="text-primary hover:underline">
                support@covermyclass.com
              </a>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────
  if (pageState === 'success') {
    const isInvite = linkType === 'invite';
    const goesToApp = isTeacherAccount || isSchoolContact;
    const redirectTo = goesToApp ? '/' : '/school/login';
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold mb-3">
            {isInvite ? 'Account ready!' : 'Password updated!'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {goesToApp
              ? 'Your password has been set. Taking you to the portal…'
              : 'Your password has been changed. Please sign in with your new password.'}
          </p>
          <Button
            className="w-full"
            onClick={() => navigate(redirectTo)}
          >
            {goesToApp ? 'Go to portal' : 'Sign in'}
          </Button>
        </div>
      </div>
    );
  }

  // ── Password form ─────────────────────────────────────────────────
  const isInvite = linkType === 'invite';
  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {isInvite ? 'Set your password' : 'Choose a new password'}
          </h1>
          <p className="text-muted-foreground">
            {isInvite
              ? 'Create a password to activate your CoverMyClass account.'
              : 'Enter a new password for your account.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="reset-password-new" className="block text-sm font-semibold mb-2">New password</label>
            <div className="relative">
              <Input
                id="reset-password-new"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                autoFocus
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="reset-password-confirm" className="block text-sm font-semibold mb-2">Confirm password</label>
            <Input
              id="reset-password-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              required
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Saving…' : isInvite ? 'Activate account' : 'Update password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
